import React from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var DOTS = "DOTS";

/**
 * Chevrons are drawn as SVG rather than the "&#8249;" / "&#8250;" glyphs so they
 * sit dead centre in the circular button. Text glyphs carry font side bearings
 * and sit on the baseline, which leaves them visually off-centre.
 */
var Chevron = function Chevron(_ref) {
  var _ref$direction = _ref.direction,
    direction = _ref$direction === void 0 ? "left" : _ref$direction;
  return /*#__PURE__*/_jsx("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    focusable: "false",
    className: "block w-[18px] h-[18px] md:w-6 md:h-6",
    children: /*#__PURE__*/_jsx("polyline", {
      points: direction === "left" ? "15 6 9 12 15 18" : "9 6 15 12 9 18"
    })
  });
};

/**
 * Builds a compact list of page numbers with "..." gaps so the control stays a
 * fixed width no matter how many pages exist.
 *
 *   total 20, current 4  -> [1, 2, 3, 4, 5, DOTS, 20]
 *   total 20, current 10 -> [1, DOTS, 9, 10, 11, DOTS, 20]
 *   total 20, current 19 -> [1, DOTS, 16, 17, 18, 19, 20]
 */
export var getPageList = function getPageList(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({
      length: totalPages
    }, function (_, i) {
      return i + 1;
    });
  }

  // Near the start: show a leading run of pages.
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, DOTS, totalPages];
  }

  // Near the end: show a trailing run of pages.
  if (currentPage >= totalPages - 3) {
    return [1, DOTS, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  // Somewhere in the middle: current page with one neighbour on each side.
  return [1, DOTS, currentPage - 1, currentPage, currentPage + 1, DOTS, totalPages];
};

/**
 * Numbered pagination control.
 *
 * Props:
 *  - currentPage  1-based current page
 *  - totalPages   total number of pages
 *  - onPageChange(page)  called with the 1-based page to move to
 *  - config       client config, used for dark mode colours
 *  - className    optional extra classes on the outer wrapper
 */
var NumberedPagination = function NumberedPagination(_ref2) {
  var _ref2$currentPage = _ref2.currentPage,
    currentPage = _ref2$currentPage === void 0 ? 1 : _ref2$currentPage,
    _ref2$totalPages = _ref2.totalPages,
    totalPages = _ref2$totalPages === void 0 ? 1 : _ref2$totalPages,
    onPageChange = _ref2.onPageChange,
    config = _ref2.config,
    _ref2$className = _ref2.className,
    className = _ref2$className === void 0 ? "" : _ref2$className;
  if (!totalPages || totalPages <= 1) {
    return null;
  }
  var isDark = !!(config !== null && config !== void 0 && config.darkMode);
  var pages = getPageList(currentPage, totalPages);
  var goTo = function goTo(page) {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange === null || onPageChange === void 0 || onPageChange(page);
  };
  var textColor = isDark ? "text-white" : "text-[#1F1F1F]";
  var mutedColor = isDark ? "text-gray-500" : "text-[#9A9A9A]";
  var arrowBase = "w-9 h-9 md:w-[52px] md:h-[52px] shrink-0 p-0 rounded-full flex items-center justify-center leading-none ".concat(isDark ? "bg-[#2E2E2E]" : "bg-[#F0F0F0]");
  // Digits sit on the baseline, so the glyph's optical centre lands slightly
  // above the line box centre. A small em-based nudge pulls it back to centre.
  var numberBase = "w-7 h-7 md:w-11 md:h-11 shrink-0 p-0 rounded-full flex items-center justify-center text-xs md:text-base leading-none";
  return /*#__PURE__*/_jsx("nav", {
    "aria-label": "Pagination",
    className: "w-full flex justify-center ".concat(className),
    children: /*#__PURE__*/_jsxs("div", {
      className: "inline-flex items-center gap-3 md:gap-8",
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: function onClick() {
          return goTo(currentPage - 1);
        },
        disabled: currentPage === 1,
        "aria-label": "Previous page",
        className: "".concat(arrowBase, " ").concat(currentPage === 1 ? "".concat(mutedColor, " cursor-not-allowed opacity-60") : "".concat(textColor, " hover:opacity-70")),
        children: /*#__PURE__*/_jsx(Chevron, {
          direction: "left"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "flex items-center gap-1 md:gap-3",
        children: pages.map(function (page, index) {
          return page === DOTS ? /*#__PURE__*/_jsx("span", {
            className: "w-4 md:w-6 text-center text-xs md:text-base ".concat(mutedColor),
            children: "\u2026"
          }, "dots-".concat(index)) : /*#__PURE__*/_jsx("button", {
            type: "button",
            onClick: function onClick() {
              return goTo(page);
            },
            "aria-current": page === currentPage ? "page" : undefined,
            className: page === currentPage ? "".concat(numberBase, " font-bold bg-primary text-white") : "".concat(numberBase, " ").concat(textColor, " hover:opacity-60"),
            children: /*#__PURE__*/_jsx("span", {
              className: "block leading-none translate-y-[0.055em]",
              children: page
            })
          }, page);
        })
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: function onClick() {
          return goTo(currentPage + 1);
        },
        disabled: currentPage === totalPages,
        "aria-label": "Next page",
        className: "".concat(arrowBase, " ").concat(currentPage === totalPages ? "".concat(mutedColor, " cursor-not-allowed opacity-60") : "".concat(textColor, " hover:opacity-70")),
        children: /*#__PURE__*/_jsx(Chevron, {
          direction: "right"
        })
      })]
    })
  });
};
export default NumberedPagination;